/**
 * CR310: S0 Scan Settings v2 - Type Definitions
 * Shared types for the tabbed scan settings interface
 */

import type { CandidateSignal } from '@/lib/flyer-pipeline-v2/types';
import type {
  DealLocatorConfig,
  TiledScanningConfig,
  ExtractionFocusConfig,
} from '@/lib/flyer-pipeline-v2/dealLocatorTypes';

// Available AI models for page scanning
export const AI_SCAN_MODELS = [
  { value: 'google/gemini-3-flash-preview', label: 'Gemini 3 Flash', shortLabel: 'Gemini 3 Flash' },
  { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash', shortLabel: 'Gemini 2.5 Flash' },
  { value: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite', shortLabel: 'Gemini Flash Lite' },
  { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro', shortLabel: 'Gemini Pro' },
  { value: 'google/gemini-3-pro-preview', label: 'Gemini 3 Pro', shortLabel: 'Gemini 3 Pro' },
  { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini', shortLabel: 'GPT-5 Mini' },
  { value: 'openai/gpt-5', label: 'GPT-5', shortLabel: 'GPT-5' },
] as const;

export type AiScanModel = typeof AI_SCAN_MODELS[number]['value'];

// CR310: Canonical OCR engine values
export const PRIMARY_OCR_ENGINES = [
  { value: 'tesseract', label: 'Tesseract (Browser)', description: 'Runs in your browser — free, no API costs. Best for cost control.' },
  { value: 'ai_vision', label: 'AI Vision OCR', description: 'Uses Gemini Flash Lite for fast, accurate text extraction. Higher cost.' },
  { value: 'paddle_ocr', label: 'PaddleOCR (Vision AI)', description: 'High-accuracy server-side OCR via PaddleOCR. Best for dense flyers. (Vision AI cost applies)' },
  { value: 'true_paddle', label: 'True Paddle OCR (Local)', description: 'Run the real PaddleOCR library on your local machine. No API costs.' },
  { value: 'none', label: 'None', description: 'Skip OCR (use for testing or when only AI deep scan is needed)' },
] as const;

export type OcrEngine = typeof PRIMARY_OCR_ENGINES[number]['value'];

// CR310: PaddleOCR advanced settings
export interface PaddleOcrConfig {
  use_angle_cls: boolean;
  det_limit_type: 'max' | 'min';
  det_limit_side_len: number;
  drop_score: number;
}

export const DEFAULT_PADDLE_OCR_CONFIG: PaddleOcrConfig = {
  use_angle_cls: false,
  det_limit_type: 'max',
  det_limit_side_len: 1280,
  drop_score: 0.5,
};

// CR317: Large Pages Config Types
export interface DownscaleApplyWhenConfig {
  enabled: boolean;
  minDimensionPx: number;
}

export interface DownscaleConfig {
  enabled: boolean;
  mode: 'max_dimension' | 'megapixels';
  maxDimensionPx: number;
  minOutputMaxDimensionPx: number; // Safety floor
  applyWhen: DownscaleApplyWhenConfig;
}

export interface TilingApplyWhenConfig {
  enabled: boolean;
  minDimensionPx: number;
}

export interface LargePagesTilingConfig {
  enabled: boolean;
  applyWhen: TilingApplyWhenConfig;
}

export interface LargePagesConfig {
  downscale: DownscaleConfig;
  tiling: LargePagesTilingConfig;
}

export const DEFAULT_LARGE_PAGES_CONFIG: LargePagesConfig = {
  downscale: {
    enabled: true,
    mode: 'max_dimension',
    maxDimensionPx: 2200,
    minOutputMaxDimensionPx: 1200, // Safety floor
    applyWhen: {
      enabled: true,
      minDimensionPx: 2600,
    },
  },
  tiling: {
    enabled: true,
    applyWhen: {
      enabled: true,
      minDimensionPx: 2600,
    },
  },
};

// CR404: Forward declaration for S12OcrConfig (defined below)

export interface S12OcrConfigPlaceholder { }

// Main batch run flyer config interface
export interface BatchRunFlyerConfig {
  // OCR settings
  ocrEngine: OcrEngine;
  minOcrScore: number;
  paddleOcr: PaddleOcrConfig;

  // CR317: Large Pages settings
  largePages: LargePagesConfig;

  // Candidate signals
  candidateThreshold: number;
  signals: CandidateSignal[];

  // Deal Locator
  dealLocator: DealLocatorConfig;
  tiledScanning: TiledScanningConfig;
  extractionFocus: ExtractionFocusConfig;

  // AI Deep Scan
  aiEnabled: boolean;
  aiModel: AiScanModel;
  maxAiCalls: number;
  maxPagesPerFlyer: number;
  maxTotalPages: number;
  aiTimeoutSeconds: number; // CR310: Renamed from aiCallTimeoutMs, now in seconds
  promptPackId: string | null;
  inputMode?: 'full_page' | 'crops_only' | 'both';

  // CR403: Execution settings (persisted separately in config_json.execution)
  execution: ExecutionConfig;

  // CR405: Flyer scan defaults (editable caps)
  flyerDefaults: FlyerScanDefaults;

  // CR404: S1.2 OCR Watchdog + Fallback settings
  s12Ocr: S12OcrConfig;

  // CR384: Flyer capture mode
  flyerCaptureMode: FlyerCaptureMode;

  // CR414: Concurrency controls
  renderConcurrency: number;
  ocrConcurrency: number;

  // CR555: Snippet Refinement (S1.4 Pass B) config
  snippetRefinement: SnippetRefinementConfig;
}

// Default signals for Batch Runs
export const DEFAULT_SIGNALS: CandidateSignal[] = [
  {
    id: 'sig_giftcard_term',
    name: "Contains 'gift card(s)'",
    type: 'KEYWORD',
    enabled: true,
    weight: 6,
    params: { terms: ['gift card', 'gift cards'], match_mode: 'ANY', case_sensitive: false }
  },
  {
    id: 'sig_currency_amount',
    name: 'Contains currency amount ($ + digits)',
    type: 'REGEX',
    enabled: true,
    weight: 1,
    params: { pattern: '\\$\\s*\\d{1,4}', min_matches: 1 }
  },
  {
    id: 'sig_verb_near_giftcard',
    name: "Qualifying verb near 'gift card'",
    type: 'PROXIMITY',
    enabled: true,
    weight: 6,
    params: {
      anchor_terms: ['gift card', 'gift cards'],
      near_terms: ['buy', 'purchase', 'spend', 'when you buy', 'when you purchase', 'get'],
      window_chars: 80
    }
  },
  {
    id: 'sig_spend_get_pattern',
    name: 'Spend/get promo pattern',
    type: 'REGEX',
    enabled: true,
    weight: 8,
    params: {
      pattern: '(spend|buy|purchase|with the purchase of)\\s*\\$?\\d{1,4}[\\s\\S]{0,120}(get|receive|earn)\\s*\\$?\\d{1,4}',
      min_matches: 1
    }
  },
  {
    id: 'sig_bonus_reward_language',
    name: 'Bonus/reward language',
    type: 'KEYWORD',
    enabled: true,
    weight: 2,
    params: { terms: ['bonus', 'get $', 'receive $', 'earn', 'with purchase'], match_mode: 'ANY', case_sensitive: false }
  },
  {
    id: 'sig_partner_offer_negative',
    name: 'Partner-offer language (negative)',
    type: 'NEGATIVE_KEYWORDS',
    enabled: true,
    weight: -6,
    params: {
      terms: ['partner offers', 'learn more', 'conditions apply', 'free trial', 'in value', 'streaming', 'hotels', 'travel', 'gas'],
      mode: 'SUBTRACT'
    }
  }
];

// Prompt pack interface
export interface PromptPackOption {
  id: string;
  name: string;
  version: number;
  is_active: boolean;
}

// CR403: Execution settings (moved from wizard to S0 config)
export interface ExecutionConfig {
  timeoutSeconds: number;
  maxConcurrentScans: number;
}

export const DEFAULT_EXECUTION_CONFIG: ExecutionConfig = {
  timeoutSeconds: 120,
  maxConcurrentScans: 3,
};

// CR405: Flyer scan defaults (editable caps)
export interface FlyerScanDefaults {
  maxPagesPerFlyer: number;
  maxTotalPagesPerRun: number;
}

export const DEFAULT_FLYER_SCAN_DEFAULTS: FlyerScanDefaults = {
  maxPagesPerFlyer: 50,
  maxTotalPagesPerRun: 1000,
};

// CR404: S1.2 OCR Watchdog + Fallback Config
export type FallbackTriggerMode = 'RETRY_DEFERRED_AT_END';
export type FallbackEngineMode = 'SAME_AS_PRIMARY' | 'SELECT_ENGINE';
export type FallbackColorMode = 'ORIGINAL' | 'GRAYSCALE';

export interface S12FallbackOcrConfig {
  enabled: boolean;
  trigger: FallbackTriggerMode;
  timeoutSeconds: number;
  engineMode: FallbackEngineMode;
  engine?: OcrEngine; // Only used when engineMode === 'SELECT_ENGINE'
  renderDpi: number;
  maxSidePx: number;
  downscaleFactor: number;
  colorMode: FallbackColorMode;
  binarizeEnabled: boolean;
  denoiseEnabled: boolean;
}

export interface S12OcrConfig {
  watchdogTimeoutSec: number; // Default 300 (5 min)
  ocrCompletionMaxTokens: number; // Default 8192 (Paddle/Vision AI completion budget)
  useFanOut: boolean;
  fanOutMinPages: number;
  fanOutConcurrency: number;
  fallback: S12FallbackOcrConfig;
}

export const DEFAULT_S12_FALLBACK_CONFIG: S12FallbackOcrConfig = {
  enabled: true,
  trigger: 'RETRY_DEFERRED_AT_END',
  timeoutSeconds: 90,
  engineMode: 'SAME_AS_PRIMARY',
  renderDpi: 150,
  maxSidePx: 1400,
  downscaleFactor: 0.75,
  colorMode: 'GRAYSCALE',
  binarizeEnabled: false,
  denoiseEnabled: false,
};

export const DEFAULT_S12_OCR_CONFIG: S12OcrConfig = {
  watchdogTimeoutSec: 300, // 5 minutes
  ocrCompletionMaxTokens: 8192,
  useFanOut: true,
  fanOutMinPages: 25,
  fanOutConcurrency: 8,
  fallback: DEFAULT_S12_FALLBACK_CONFIG,
};

// CR384 / CR450: Flyer Capture Mode
export const FLYER_CAPTURE_MODES = [
  { value: 'DOWNLOAD_ONLY', label: 'Flyer download (PDF → Render → OCR)', helper: 'Use S0.5 Download + S1.0/S1.1 (current behavior path when S0.5 is enabled).', comingSoon: false },
  // CR450: IMAGE_CAPTURE — merchant-based adapter pipeline, skips S1.0/S1.1
  { value: 'IMAGE_CAPTURE', label: 'Flyer Image Scan (URL → Image Capture)', helper: 'Merchant-based image capture via adapters. Skips S1.0 + S1.1; captured pages feed directly into S1.2 OCR.', comingSoon: false },
  { value: 'IMAGE_ONLY', label: 'Flyer image only (coming soon)', helper: 'Planned: use pre-rendered images and skip S1.0/S1.1.', comingSoon: true },
  { value: 'IMAGE_AND_DOWNLOAD', label: 'Both (download + image) — coming soon', helper: 'Planned: run both pipelines for redundancy.', comingSoon: true },
] as const;

export type FlyerCaptureMode = typeof FLYER_CAPTURE_MODES[number]['value'];

export const DEFAULT_FLYER_CAPTURE_MODE: FlyerCaptureMode = 'DOWNLOAD_ONLY';

// CR555: Snippet Refinement (S1.4 Cropper Pass B) config
export interface SnippetRefinementConfig {
  enabled: boolean;
  min_bbox_height_ratio: number;
  min_evidence_for_pass_b: number;
  min_evidence_for_override: number;
  max_per_batch: number | null;
}

export const DEFAULT_SNIPPET_REFINEMENT_CONFIG: SnippetRefinementConfig = {
  enabled: true,
  min_bbox_height_ratio: 0.03,
  min_evidence_for_pass_b: 2,
  min_evidence_for_override: 4,
  max_per_batch: null,
};

// Tab IDs
export type S0TabId = 'scan' | 'ocr' | 'large-pages' | 'signals' | 'locator' | 'ai';
